import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import dateFormat from "dateformat";
import QRCode from "qrcode";
import failed from "../assets/Failure.png";
import cred from "../assets/cred.svg";
import othersLogo from "../assets/Others 2x.png";
import gpay from "../assets/gpay.png";
import payloader from "../assets/payloader.gif";
import paytm from "../assets/paytm.png";
import phonepe from "../assets/phonepe.png";
import upiId from "../assets/upiId.svg";
import upiFull from "../assets/upi_full.png";
import DataContext from "../contexts/DataProvider";
import FormDataContext from "../contexts/FormData";
import FormatAmount from "../helper/FormatAmount";
import GetAPI from "../helper/GetAPI";
import { isIOS } from "react-device-detect";
import SwinkSpinner from "./SwinkSpinner";
import analytics from "../helper/analytics";

const simpleEmailPattern = /^[^@]+@[^@]+$/;

// Mask a UPI ID for fraud logging — keep the PSP handle, mask the identity part.
// "9876543210@paytm" -> "98***0@paytm" ; "johndoe@oksbi" -> "jo***e@oksbi"
const maskVpa = (vpa) => {
  const at = (vpa || "").indexOf("@");
  if (at < 1) return null;
  const user = vpa.slice(0, at);
  const handle = vpa.slice(at + 1);
  if (!handle) return null;
  const u = user.length <= 3 ? user[0] + "**" : user.slice(0, 2) + "***" + user.slice(-1);
  return `${u}@${handle}`;
};

const UPI = ({ selectedPayType, bottomRef, setIsNavigating }) => {
  const [upiID, setUpiID] = useState("");
  const [activeUpiTab, setActiveUpiTab] = useState("");
  const [appspay, setAppspay] = useState(false);
  const [appNotInstalled, setAppNotInstalled] = useState(null); // string label or null
  const [deepLinkUrl, setDeepLinkUrl] = useState(null);   // cached UPI string after first tap
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [nameAtBank, setNameAtBank] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const { formData, setFormData, setShowProcessingScreen } = useContext(FormDataContext);
  const [upiRequestLoading, setUpiRequestLoading] = useState(false);
  const { data, setData } = useContext(DataContext);
  const bottommRef = useRef(null);
  // Fraud signal: distinct masked VPAs entered this session (VPA enumeration / testing).
  const seenVpasRef = useRef(new Set());
  let modeOfPayment = "UPI";
  const [qrCodeError, setQrCodeError] = useState("");
  const [qrRequested, setQrRequested] = useState(false);

  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (!window.matchMedia) return;
    setIsTouchDevice(window.matchMedia("(pointer:coarse)").matches);
  }, []);

  // Set initial active tab based on device type and enabled modes
  useEffect(() => {
    if (isTouchDevice) {
      setActiveUpiTab(data.upiDeepLink ? "deeplink" : "collect");
    } else {
      setActiveUpiTab(data.upiDQR ? "qr" : "collect");
    }
  }, [isTouchDevice, data.upiDeepLink, data.upiDQR]);


  const handleUpi = (event) => {
    if (!event.target.value) {
      setData({ ...data, errorMessage: "" });
    }
    const result = event.target.value.replace(/[^a-zA-Z0-9.@_-]/g, "");
    setUpiID(result);
    const isSimpleValid = simpleEmailPattern.test(result);
    if (data.upiPG === 8) {
      setFormData({
        type: isSimpleValid ? "UPI" : "",
        upiID: result,
        modeOfPayment: modeOfPayment,
        nextStep: isSimpleValid ? "REQUESTMONEY" : "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    analytics.track("upi_vpa_verify_attempt");
    setUpiRequestLoading(true);
    const validatePayload = {
      pgID: 8,
      vpa: upiID,
      mID: data?.mid,
      returnURL: data?.returnURL,
      customerName: "",
    };
    const apbValidatePayload = {
      cVPA: upiID,
      terminalId: data?.tid,
      returnURL: data?.returnURL,
    };
    if (data?.upiPG === 8) {
      axios
        .post(GetAPI("validateVPA", data?.upiPG), validatePayload, {
          headers: {
            channel: 14,
            auth_token: "FREFA45D$B2#18842765#992",
            "session-token": data?.token,
            "Content-Type": "application/json",
          },
        })
        .then(function (response) {
          const data = response.data;
          if (data?.data?.accountExists === "YES") {
            setNameAtBank(data?.data?.nameAtBank);
          } else {
            setErrorMsg("Verification Failed");
          }
          setUpiRequestLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setErrorMsg("Verification Failed");
          setUpiRequestLoading(false);
        });
    } else if (data?.upiPG === 12) {
      axios
        .post(GetAPI("apbValidateVPA", data?.upiPG), apbValidatePayload, {
          headers: {
            channel: 14,
            auth_token: "FREFA45D$B2#18842765#992",
            "session-token": data?.token,
            "Content-Type": "application/json",
          },
        })
        .then(function (response) {
          const data = response.data;
          if (data?.data?.name) {
            setFormData({
              type: "UPI",
              upiID: upiID,
              modeOfPayment: modeOfPayment,
              nextStep: "REQUESTMONEY",
            });
            setNameAtBank(data?.data?.name);
          } else {
            setErrorMsg("Verification Failed");
            setNameAtBank("");
          }
          setUpiRequestLoading(false);
        })
        .catch((e) => {
          console.log(e);
          setErrorMsg("Verification Failed");
          setUpiRequestLoading(false);
        });
    }
  };

  // Reset QR state when switching away from QR tab
  useEffect(() => {
    if (activeUpiTab !== "qr") {
      setQrRequested(false);
      setQrCodeUrl("");
      setQrCodeError("");
    }
  }, [activeUpiTab]);

  // Activity tracking: time spent on each UPI sub-section (collect / qr / deeplink).
  // useLayoutEffect so the section updates before mount-time child events get tagged.
  useLayoutEffect(() => {
    if (activeUpiTab) analytics.enterSection(`upi_${activeUpiTab}`);
  }, [activeUpiTab]);

  const generateDynamicQrCode = async () => {
    try {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      let axiosBody = {
        amount: FormatAmount(data.totalAmount),
        invoiceNumber: data.transactionID,
        terminalID: data.tid,
        txnID: data.txnid,
        returnURL: data?.returnURL,
        merReturnURL: data?.merReturnURL,
        userName: data?.userName || "",
        userMobile: data?.customerMobile || data?.userMobile || "",
        userEmail: data?.customerEmail || data?.userEmail || "",
        userAddress: data?.userAddress || "",
        dateAndTime: dateFormat(Date(), "yyyy-mm-dd hh:MM:ss").toString(),
      };
      let axiosHeaders = {
        channel: 14,
        "device-id": data.divid,
        auth_token: "FREFA45D$B2#18842765#992",
        "Content-Type": "application/json",
      };
      let axiosReponse = await axios.post(GetAPI("qrCode"), axiosBody, { headers: axiosHeaders });
      if (axiosReponse?.data?.status === 0) {
        let qrCode = axiosReponse?.data?.data?.qrStringData;
        try {
          const url = await QRCode.toDataURL(qrCode);
          setQrCodeUrl(url);
          analytics.track("upi_qr_generated");
          bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
        } catch (err) {
          analytics.track("upi_qr_failed");
          setQrCodeError("Failed to generate QR Code.");
        }
        setFormData({ type: "UPI", modeOfPayment: "QRCODE", nextStep: "CHECKSTATUS" });
      } else {
        setQrCodeError("Failed to generate QR Code.");
      }
    } catch (Err) {
      setQrCodeError("Failed to generate QR Code.");
    }
  };

  useEffect(() => {
    if (selectedPayType !== "UPI") {
      setFormData({ type: "", modeOfPayment: "", nextStep: "" });
      setUpiID("");
    }
  }, [selectedPayType]);

  const upiClassname3 =
    formData?.nextStep === "CHECKSTATUS" || appspay
      ? "pointer-events-none opacity-50"
      : "";

  const imageClick = (appname, suffix = "", appLabel = "") => {
    analytics.track("upi_app_tap", { app: appLabel, path: "async" });
    analytics.flush("checkpoint_upi_app_tap"); // safety: about to leave for the UPI app
    setUpiRequestLoading(false);
    setAppspay(true);
    setAppNotInstalled(null);
    let qrCode = null;
    axios
      .post(
        GetAPI("qrCode"),
        {
          amount: FormatAmount(data.totalAmount),
          invoiceNumber: data.transactionID,
          terminalID: data.tid,
          txnID: data.txnid,
          returnURL: data?.returnURL,
          merReturnURL: data?.merReturnURL,
          userName: data?.userName || "",
          userMobile: data?.customerMobile || data?.userMobile || "",
          userEmail: data?.customerEmail || data?.userEmail || "",
          userAddress: data?.userAddress || "",
          dateAndTime: dateFormat(Date(), "yyyy-mm-dd hh:MM:ss").toString(),
        },
        {
          headers: {
            channel: 14,
            "device-id": data.divid,
            auth_token: "FREFA45D$B2#18842765#992",
            "Content-Type": "application/json",
          },
        }
      )
      .then(function (response) {
        if (response?.data?.status === 0) {
          qrCode = response?.data?.data?.qrStringData;
          let updatedUrl;
          if (data?.upiPG === 12) {
            const urlObj = new URL(qrCode);
            const params = new URLSearchParams(urlObj.search);
            params.set("mode", "00");
            urlObj.search = params.toString();
            updatedUrl = urlObj.toString();
          } else {
            updatedUrl = qrCode;
          }
          setDeepLinkUrl(updatedUrl); // cache for future taps
          let dynamicLinker = updatedUrl?.slice(6);
          setIsNavigating(true);
          window._swinkpay_is_navigating = true;
          window.location.href = appname + dynamicLinker + suffix;
          setFormData({ type: "UPI", modeOfPayment: "QRCODE", nextStep: "CHECKSTATUS" });

          // Detect if the app actually opened.
          // If the page never goes hidden after 2.5 s the app is not installed.
          let appOpened = false;
          const onHide = () => { if (document.hidden) appOpened = true; };
          document.addEventListener("visibilitychange", onHide);
          setTimeout(() => {
            document.removeEventListener("visibilitychange", onHide);
            const isVisible = !document.hidden;

            if (!appOpened) {
              setAppspay(false);
              if (appLabel !== "Others") {
                analytics.track("upi_app_not_installed", { app: appLabel });
                setFormData({ type: "", modeOfPayment: "", nextStep: "" });
                setAppNotInstalled(appLabel);
              } else {
                analytics.track("upi_app_launch_unconfirmed", { app: appLabel });
                setFormData({ type: "", modeOfPayment: "", nextStep: "" });
              }
            } else {
              // App opened (page went hidden at least once)
              setAppspay(false);
              if (isVisible) {
                // User already returned within 2.5s - likely canceled
                analytics.track("upi_app_returned_early", { app: appLabel });
                setFormData({ type: "", modeOfPayment: "", nextStep: "" });
              } else {
                // User is still in the app
                analytics.track("upi_app_opened", { app: appLabel });
                setShowProcessingScreen(true);
              }
            }
          }, 2500);
        } else {
          setAppspay(false);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  };

  const handleCopyPaste = (e) => {
    e.preventDefault();
  };

  // Build tab list based on device and enabled modes
  const desktopTabs = [
    data.upiDQR && { id: "qr", label: "Scan QR Code" },
    data.upiCollect && { id: "collect", label: "Enter UPI ID" },
  ].filter(Boolean);

  const mobileTabs = [
    data.upiDeepLink && { id: "deeplink", label: "Pay via App" },
    data.upiCollect && { id: "collect", label: "UPI ID" },
  ].filter(Boolean);

  const tabs = isTouchDevice ? mobileTabs : desktopTabs;
  const showTabs = tabs.length > 1;

  const cornerBrackets = [
    "top-0 left-0 border-t-2 border-l-2 rounded-tl-lg",
    "top-0 right-0 border-t-2 border-r-2 rounded-tr-lg",
    "bottom-0 left-0 border-b-2 border-l-2 rounded-bl-lg",
    "bottom-0 right-0 border-b-2 border-r-2 rounded-br-lg",
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>

      {/* ── Segmented tab switcher ── */}
      {showTabs && (
        <div className="flex p-1 mx-2 mt-2 mb-1 rounded-2xl gap-1 flex-shrink-0"
             style={{ background: "rgba(0,0,0,0.05)" }}>
          {tabs.map((tab) => {
            const isDisabled = qrRequested && tab.id !== "qr";
            return (
              <button
                key={tab.id}
                type="button"
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) return;
                  setActiveUpiTab(tab.id);
                  if (tab.id !== "qr") { setQrCodeUrl(""); setQrCodeError(""); }
                }}
                className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all duration-200 outline-none focus:outline-none ${
                  activeUpiTab === tab.id
                    ? "bg-white text-gray-900 shadow-sm"
                    : isDisabled
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-500"
                }`}
              >
                {tab.label}
                {isDisabled && <span className="ml-1 text-[9px] font-normal opacity-60">locked</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* ── QR Code content (desktop) ── */}
      {!isTouchDevice && activeUpiTab === "qr" && (
        // Scroll container (was overflow:hidden + justify-center, which clipped the QR content
        // top & bottom once the Offers strip + Order Summary shortened the panel). The inner
        // min-height:100% wrapper keeps it vertically centered when there's room, and lets it
        // scroll instead of clipping when there isn't.
        <div style={{ width: '100%', padding: '12px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
          <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>

          {!qrRequested ? (
            /* ── Pre-generate: prompt the user to click ── */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', gap: 16 }}>
              {/* Dashed QR placeholder */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setQrRequested(true); generateDynamicQrCode(); }}
                  className="w-[min(128px,34vw)] h-[min(128px,34vw)] rounded-2xl flex items-center justify-center cursor-pointer transition-all active:scale-95 hover:border-opacity-80"
                  style={{ border: "2.5px dashed rgba(30,86,200,0.6)", background: "rgba(30,86,200,0.04)" }}
                >
                  {cornerBrackets.map((cls, i) => (
                    <div key={i} className={`absolute w-5 h-5 ${cls}`} style={{ borderColor: "#1E56C8" }} />
                  ))}
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(30,86,200,0.12)" }}>
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                        <rect x="3" y="3" width="7" height="7" rx="1" stroke="#1E56C8" strokeWidth="1.8"/>
                        <rect x="14" y="3" width="7" height="7" rx="1" stroke="#1E56C8" strokeWidth="1.8"/>
                        <rect x="3" y="14" width="7" height="7" rx="1" stroke="#1E56C8" strokeWidth="1.8"/>
                        <rect x="5" y="5" width="3" height="3" rx="0.5" fill="#1E56C8"/>
                        <rect x="16" y="5" width="3" height="3" rx="0.5" fill="#1E56C8"/>
                        <rect x="5" y="16" width="3" height="3" rx="0.5" fill="#1E56C8"/>
                        <path d="M14 14h2v2h-2zM18 14h3M18 18h3M14 18v3" stroke="#1E56C8" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <span className="text-[9px] text-[#1E56C8] font-semibold text-center leading-tight">Tap to<br/>generate</span>
                  </div>
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700 mb-1">Pay by scanning QR Code</p>
                <p className="text-xs text-gray-400">Works with any UPI app — GPay, PhonePe, Paytm, BHIM & more</p>
              </div>

              {/* Generate QR button */}
              <button
                type="button"
                onClick={() => {
                  setQrRequested(true);
                  generateDynamicQrCode();
                }}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-white text-sm font-semibold transition-all active:scale-95"
                style={{ background: "linear-gradient(135deg, #1E56C8, #1340A0)", boxShadow: "0 4px 14px rgba(30,86,200,0.35)" }}
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
                  <rect x="3" y="3" width="7" height="7" rx="1" stroke="white" strokeWidth="1.8"/>
                  <rect x="14" y="3" width="7" height="7" rx="1" stroke="white" strokeWidth="1.8"/>
                  <rect x="3" y="14" width="7" height="7" rx="1" stroke="white" strokeWidth="1.8"/>
                  <rect x="5" y="5" width="3" height="3" rx="0.5" fill="white"/>
                  <rect x="16" y="5" width="3" height="3" rx="0.5" fill="white"/>
                  <rect x="5" y="16" width="3" height="3" rx="0.5" fill="white"/>
                  <path d="M14 14h2v2h-2zM18 14h3M18 18h3M14 18v3" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                Generate QR Code
              </button>

              {/* Supported app logos */}
              <div className="w-full">
                <p className="text-[10px] text-gray-400 text-center mb-1.5">Scan with any UPI App</p>
                <div className="logo-ticker-wrap w-full">
                  <div className="logo-ticker items-center gap-6 py-0.5">
                    {[phonepe, gpay, paytm, cred, upiFull, phonepe, gpay, paytm, cred, upiFull].map((src, i) => (
                      <img key={i} src={src} alt="upi-app" className="h-5 w-auto flex-shrink-0 opacity-70" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* ── Post-generate: show loading / QR / error ── */
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 12 }}>
              {/* Step indicators */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                {[
                  { step: "1", label: "Open UPI App" },
                  { step: "2", label: "Scan QR" },
                  { step: "3", label: `Pay ₹${FormatAmount(data.totalAmount)}` },
                ].map(({ step, label }, i, arr) => (
                  <div key={step} className="flex items-center gap-1">
                    <div className="flex flex-col items-center gap-0.5">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ background: "#1E56C8" }}>
                        {step}
                      </div>
                      <span className="text-[10px] text-gray-400 whitespace-nowrap">{label}</span>
                    </div>
                    {i < arr.length - 1 && <div className="w-5 h-px bg-gray-200 mb-4 mx-0.5" />}
                  </div>
                ))}
              </div>

              {/* QR frame */}
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <div className="relative p-1">
                  {cornerBrackets.map((cls, i) => (
                    <div key={i} className={`absolute w-5 h-5 ${cls}`} style={{ borderColor: "#1E56C8" }} />
                  ))}
                  <div className="bg-white rounded-lg shadow-md p-2">
                    {qrCodeUrl ? (
                      <img src={qrCodeUrl} alt="qrcode" className="w-[min(160px,38vw)] h-[min(160px,38vw)] object-contain" />
                    ) : qrCodeError ? (
                      <div className="w-[min(160px,38vw)] h-[min(160px,38vw)] flex items-center justify-center">
                        <img src={failed} className="h-16 w-auto" alt="failed" />
                      </div>
                    ) : (
                      <div className="w-[min(160px,38vw)] h-[min(160px,38vw)] flex flex-col items-center justify-center gap-2">
                        <SwinkSpinner size={46} />
                        <span className="text-[10px] text-gray-400">Generating QR...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {qrCodeError ? (
                <p className="text-xs text-center text-[#E80F21] font-semibold">{qrCodeError}</p>
              ) : (
                <>
                  <p className="text-center text-xs text-gray-400">Point your camera at the QR code to pay instantly</p>
                  <div className="w-full mt-1">
                    <div className="logo-ticker-wrap w-full">
                      <div className="logo-ticker items-center gap-6 py-0.5">
                        {[phonepe, gpay, paytm, cred, upiFull, phonepe, gpay, paytm, cred, upiFull].map((src, i) => (
                          <img key={i} src={src} alt="upi-app" className="h-5 w-auto flex-shrink-0 opacity-70" />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      )}

      {/* ── UPI Collect content (desktop + mobile) ── */}
      {activeUpiTab === "collect" && (
        <div className={`hide-scrollbar ${upiClassname3}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', width: '100%', padding: '4px 12px 8px', overflowY: 'auto', flex: 1, minHeight: 0 }}>

          {/* App grid — 3 columns, fits all screen sizes */}
          <div className="mb-3">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Pay with any UPI app</p>
            <div className="grid grid-cols-3 gap-x-2 gap-y-3">
              {[
                { src: upiFull, label: "BHIM",       bg: "rgba(232,127,35,0.07)",  border: "rgba(232,127,35,0.22)"   },
                { src: gpay,    label: "Google Pay", bg: "rgba(66,133,244,0.09)",  border: "rgba(66,133,244,0.22)"  },
                { src: phonepe, label: "PhonePe",    bg: "rgba(99,42,174,0.09)",   border: "rgba(99,42,174,0.22)"   },
                { src: paytm,   label: "Paytm",      bg: "rgba(0,162,222,0.09)",   border: "rgba(0,162,222,0.22)"   },
                { src: cred,    label: "CRED",       bg: "rgba(10,10,10,0.05)",    border: "rgba(10,10,10,0.15)"    },
                { src: othersLogo, label: "Others",  bg: "rgba(30,86,200,0.07)",   border: "rgba(30,86,200,0.2)"    },
              ].map(({ src, label, bg, border }) => (
                <div key={label} className="flex flex-col items-center gap-1.5">
                  <div className="w-full max-w-[66px] aspect-square rounded-2xl flex items-center justify-center mx-auto"
                       style={{ background: bg, border: `1.5px solid ${border}` }}>
                    <img src={src} alt={label} className={`${label === "BHIM" ? "w-[76%] h-auto" : "h-8 w-8"} object-contain`} />
                  </div>
                  <span className="text-[10px] font-semibold text-gray-500 leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* UPI ID input */}
          <div className="relative mb-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
              <img src={upiId} alt="upi" className="h-4 w-4 opacity-40" />
            </div>
            <input
              type="text"
              className="input-modern w-full rounded-xl border border-gray-200 bg-white pl-9 pr-10 py-2 text-sm font-medium placeholder:text-gray-300 text-gray-800"
              placeholder="yourname@bank"
              disabled={formData?.nextStep === "CHECKSTATUS"}
              value={upiID}
              onChange={handleUpi}
              onBlur={() => {
                // Fraud: log each DISTINCT valid VPA the user entered (masked).
                if (!simpleEmailPattern.test(upiID)) return;
                const masked = maskVpa(upiID);
                if (masked && !seenVpasRef.current.has(masked)) {
                  seenVpasRef.current.add(masked);
                  analytics.track("upi_attempt", { attemptNo: seenVpasRef.current.size, maskedVpa: masked });
                }
              }}
              minLength="3"
              maxLength="50"
              style={{ fontSize: 16 }}
              onCopy={handleCopyPaste}
              onPaste={handleCopyPaste}
              onCut={handleCopyPaste}
            />
            {/* Validation dot */}
            {upiID && (
              <div
                className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full"
                style={{ background: simpleEmailPattern.test(upiID) ? "#1E56C8" : "#f59e0b" }}
              />
            )}
          </div>

          {/* Status / hint line */}
          <div className="mb-2 min-h-[16px]">
            {nameAtBank ? (
              <div className="flex items-center gap-1.5 px-1">
                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#1E56C8" }}>
                  <svg viewBox="0 0 10 10" className="w-2 h-2"><polyline points="2,5 4,7 8,3" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="text-xs font-semibold text-[#1340A0]">Verified: {nameAtBank}</span>
              </div>
            ) : (errorMsg || data.errorMessage) ? (
              <p className="text-xs text-[#E80F21] px-1">{errorMsg || data.errorMessage}</p>
            ) : (
              <p className="text-[11px] text-gray-400 px-1">You'll receive a collect request on your UPI app</p>
            )}
          </div>

          {/* Verify button — only when verifyUPICollect enabled */}
          {data.verifyUPICollect && (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!upiID || !simpleEmailPattern.test(upiID) || formData?.nextStep === "CHECKSTATUS" || upiRequestLoading}
              className="w-full py-2.5 rounded-xl text-white text-sm font-semibold mb-4 transition-all active:scale-[0.98] flex items-center justify-center gap-2"
              style={{ background: (upiID && simpleEmailPattern.test(upiID)) ? "#1E56C8" : "#d1d5db" }}
            >
              {upiRequestLoading ? (
                <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Verifying…</>
              ) : "Verify UPI ID"}
            </button>
          )}

          {/* How it works */}
          <div className="rounded-2xl overflow-hidden flex-shrink-0" style={{ background: "rgba(30,86,200,0.05)", border: "1px solid rgba(30,86,200,0.12)" }}>
            <div className="px-3 pt-2 pb-0.5">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1">How it works</p>
            </div>
            {[
              { n: "1", title: "Enter UPI ID", desc: "Type your UPI handle (e.g. name@bank)" },
              { n: "2", title: "Request sent", desc: "We send a collect request to your app" },
              { n: "3", title: "Approve & pay", desc: "Open your UPI app and approve the request" },
            ].map(({ n, title, desc }, i, arr) => (
              <div key={n}>
                <div className="flex items-start gap-3 px-3 py-1.5">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5"
                       style={{ background: "linear-gradient(135deg, #1E56C8, #1340A0)" }}>
                    {n}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-700 leading-tight">{title}</p>
                    <p className="text-[11px] text-gray-400 leading-tight mt-0.5">{desc}</p>
                  </div>
                </div>
                {i < arr.length - 1 && <div className="h-px mx-3" style={{ background: "rgba(30,86,200,0.12)" }} />}
              </div>
            ))}
            <div className="pb-1" />
          </div>
        </div>
      )}

      {/* ── Mobile deep-link app buttons ── */}
      {isTouchDevice && activeUpiTab === "deeplink" && data.upiDeepLink && (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: 12, gap: 10 }}>

          {/* Full-screen overlay — shown while waiting for app to open */}
          {appspay && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'white',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              padding: '0 24px',
            }}>
              {/* Ring-pulse spinner */}
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{ position: 'absolute', width: 88, height: 88, borderRadius: '50%',
                              background: '#1E56C8', opacity: 0,
                              animation: 'ring-pulse 2s 0.3s ease-out infinite' }} />
                <div style={{ width: 64, height: 64, borderRadius: '50%', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(30,86,200,0.3)',
                              background: 'linear-gradient(135deg, #1E56C8, #1340A0)' }}>
                  <svg viewBox="0 0 24 24" style={{ width: 32, height: 32, animation: 'spin 2s linear infinite' }} fill="none">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
                          stroke="white" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              <p style={{ fontSize: 16, fontWeight: 700, color: '#1a2d5a', margin: '0 0 6px', textAlign: 'center' }}>
                Opening your UPI app…
              </p>
              <p style={{ fontSize: 12, color: '#6b7fa8', margin: '0 0 28px', textAlign: 'center' }}>
                Complete your payment in the app
              </p>
              {/* Amber warning */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                            background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.25)',
                            borderRadius: 12, padding: '10px 18px', maxWidth: 320 }}>
                <svg viewBox="0 0 24 24" style={{ width: 16, height: 16, flexShrink: 0 }} fill="none">
                  <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p style={{ fontSize: 12, fontWeight: 500, color: '#92400e', margin: 0 }}>
                  Do not press back or close this window
                </p>
              </div>
            </div>
          )}

          {/* Not-installed banner — shown inline, grid stays visible below */}
          {appNotInstalled && (
            <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl"
                 style={{ background: "rgba(232,15,33,0.06)", border: "1px solid rgba(232,15,33,0.18)" }}>
              <svg viewBox="0 0 20 20" className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none">
                <circle cx="10" cy="10" r="8" stroke="#E80F21" strokeWidth="1.6"/>
                <path d="M10 6v4" stroke="#E80F21" strokeWidth="1.8" strokeLinecap="round"/>
                <circle cx="10" cy="13.5" r="0.9" fill="#E80F21"/>
              </svg>
              <p className="text-xs text-[#E80F21] font-medium flex-1 leading-snug">
                <span className="font-bold">{appNotInstalled}</span> is not installed.
                {" "}Please tap another app below.
              </p>
              <button type="button" onClick={() => setAppNotInstalled(null)} className="text-[#E80F21] opacity-60 flex-shrink-0 mt-0.5">
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          )}

          {/* Title */}
          {!appspay && (
            <p className="text-xs text-gray-400 text-center">
              Choose your UPI app to pay ₹{FormatAmount(data.totalAmount)}
            </p>
          )}

          {/* App grid — always visible; faded while loading/opening */}
          <div className={`grid grid-cols-3 gap-x-3 gap-y-3 w-full transition-opacity ${appspay ? "pointer-events-none opacity-40" : ""}`}>
            {[
              { src: upiFull,  label: "BHIM",    scheme: "bhim://",                                 bg: "rgba(232,127,35,0.07)",  border: "rgba(232,127,35,0.22)"  },
              { src: gpay,    label: "Google Pay", scheme: "tez://upi/", iosScheme: "gpay://upi/",  bg: "rgba(66,133,244,0.09)",  border: "rgba(66,133,244,0.22)" },
              { src: phonepe, label: "PhonePe", scheme: "phonepe://",                              bg: "rgba(99,42,174,0.09)",   border: "rgba(99,42,174,0.22)"  },
              { src: paytm,   label: "Paytm",   scheme: "paytmmp://upi/",                          bg: "rgba(0,162,222,0.09)",   border: "rgba(0,162,222,0.22)"  },
              { src: cred,    label: "CRED",    scheme: "credpay://upi/",                          bg: "rgba(10,10,10,0.05)",    border: "rgba(10,10,10,0.15)"   },
              { src: othersLogo, label: "Others",   scheme: "upi://",                                  bg: "rgba(30,86,200,0.07)",   border: "rgba(30,86,200,0.2)"   },
            ].map(({ src, label, scheme, suffix = "", iosScheme, iosSuffix, bg, border }) => {
              const isNotInstalled = appNotInstalled === label;
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    const resolvedScheme = (isIOS && iosScheme) ? iosScheme : scheme;
                    const resolvedSuffix = (isIOS && iosScheme) ? (iosSuffix ?? "") : (suffix ?? "");

                    if (deepLinkUrl) {
                      // Synchronous path — URL already cached; needed on iOS to avoid
                      // the browser blocking window.location.href after a failed scheme attempt.
                      analytics.track("upi_app_tap", { app: label, path: "cached" });
                      analytics.flush("checkpoint_upi_app_tap"); // safety: about to leave for the UPI app
                      const dynamicLinker = deepLinkUrl.slice(6);
                      setAppspay(true);
                      setAppNotInstalled(null);
                      setIsNavigating(true);
                      window._swinkpay_is_navigating = true;
                      window.location.href = resolvedScheme + dynamicLinker + resolvedSuffix;
                      setFormData({ type: "UPI", modeOfPayment: "QRCODE", nextStep: "CHECKSTATUS" });
                      // Detect if the app actually opened
                      let appOpened = false;
                      const onHide = () => { if (document.hidden) appOpened = true; };
                      document.addEventListener("visibilitychange", onHide);
                      setTimeout(() => {
                        document.removeEventListener("visibilitychange", onHide);
                        const isVisible = !document.hidden;

                        if (!appOpened) {
                          setAppspay(false);
                          if (label !== "Others") {
                            analytics.track("upi_app_not_installed", { app: label });
                            setFormData({ type: "", modeOfPayment: "", nextStep: "" });
                            setAppNotInstalled(label);
                          } else {
                            analytics.track("upi_app_launch_unconfirmed", { app: label });
                            setFormData({ type: "", modeOfPayment: "", nextStep: "" });
                          }
                        } else {
                          // App opened (page went hidden at least once)
                          setAppspay(false);
                          if (isVisible) {
                            // User returned within 2.5s - likely canceled
                            analytics.track("upi_app_returned_early", { app: label });
                            setFormData({ type: "", modeOfPayment: "", nextStep: "" });
                          } else {
                            // User is still away in the app
                            analytics.track("upi_app_opened", { app: label });
                            setShowProcessingScreen(true);
                          }
                        }
                      }, 2500);
                    } else {
                      // Async fallback — pre-fetch not ready; original imageClick behaviour.
                      imageClick(resolvedScheme, resolvedSuffix, label);
                    }
                  }}
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <div className="w-full max-w-[68px] aspect-square rounded-2xl flex items-center justify-center relative transition-all group-active:scale-95 mx-auto"
                       style={{ background: isNotInstalled ? "rgba(232,15,33,0.06)" : bg, border: `1.5px solid ${isNotInstalled ? "rgba(232,15,33,0.3)" : border}` }}>
                    <img src={src} alt={label} className={`${label === "BHIM" ? "w-[76%] h-auto" : "h-8 w-8"} object-contain ${isNotInstalled ? "opacity-40" : ""}`} />
                    {isNotInstalled && (
                      <div className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: "#E80F21" }}>
                        <svg viewBox="0 0 10 10" className="w-2.5 h-2.5" fill="none">
                          <path d="M3 3l4 4M7 3l-4 4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <span className={`text-[11px] font-semibold ${isNotInstalled ? "text-[#E80F21]" : "text-gray-600"}`}>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div ref={bottommRef} />
    </div>
  );
};

export default UPI;
